import { marked } from 'marked';

const ARABIC_SCRIPT = /[\u0600-\u06ff]/;

/*
 * Technical vocabulary stays in English in the Arabic edition.  A bare Latin
 * run inside an RTL paragraph is normally readable, but neighbouring Arabic
 * punctuation can make the browser place it on the wrong side of the phrase.
 * `bdi` gives those terms a direction of their own without making them look
 * like commands.  Commands and identifiers already live in `code`, where the
 * stronger machine-text rules apply, so they are deliberately skipped here.
 */
const TECHNICAL_TERM =
  /(?<![\p{L}\p{N}_])(?:pull requests?|feature flags?|commit messages?|merge commits?|force pushes?|flaky tests?|working tree|staging area|CI pipelines?|CI runners?|Git hooks?|dead code|cherry-pick|dependencies|dependency|repositories|repository|branches|branch|commits|commit|codebase|scripts?|pickaxe|workflows?|runners?|checkout|rebase|merge|push|pull|fetch|stash|reflog|backfill|hotfix|rollback|deploy|runbook|lockfile|benchmark|terminal|shell|parent|hashes?|hooks?|fork|origin|upstream|GitHub|HEAD|main|API|CI|Git|on|off)(?![\p{L}\p{N}_])/giu;

function isolateTechnicalTerms(html: string): string {
  let literalDepth = 0;

  return html
    .split(/(<[^>]+>)/g)
    .map((part) => {
      if (part.startsWith('<')) {
        if (/^<\/(?:code|pre)\s*>/i.test(part)) literalDepth = Math.max(0, literalDepth - 1);
        else if (/^<(?:code|pre)(?:\s|>)/i.test(part)) literalDepth += 1;
        return part;
      }
      if (literalDepth) return part;
      return part.replace(TECHNICAL_TERM, (term) => `<bdi class="tech-term" dir="ltr">${term}</bdi>`);
    })
    .join('');
}

/**
 * Markdown rendering for mission text.
 *
 * The only HTML this application injects comes through here. The input is
 * always author-written content from the repository's own mission YAML, never
 * anything a learner or the workspace produced, which is what makes it safe.
 * Keeping it in one tiny module means that claim can be audited in one place.
 */
export function md(text: string): { __html: string } {
  const html = marked.parse(text, { async: false }) as string;
  return { __html: ARABIC_SCRIPT.test(text) ? isolateTechnicalTerms(html) : html };
}
